import { Autocomplete, Box, IconButton, Stack, TextField } from '@mui/material';
import { SyntheticEvent, useEffect, useState, useMemo } from 'react';
import { useFacetValues } from '../../hooks/useFacetValues';
import { FacetParams } from '../../hooks/queryKeys';
import { useOktaAuth } from '@okta/okta-react';
import { toast } from 'react-toastify';
import IAttributeDto from '../../models/iAttributeDto';
import IFilterValue from '../../models/iFilterValue';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useSearch } from '../../context/SearchContext';
import IFacetValue from '../../models/iFacetValue';
import ISalesOrg from '../../models/iSalesOrg';

export interface IAttributeVaue {
  attributeRef?: React.RefObject<HTMLDivElement>;
  filter: IFilterValue;
  handleRemove: () => void;
  handleValueKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled: boolean;
  autoFocusValue?: boolean;
}

export const AttributeValueSelector = (props: IAttributeVaue) => {
  const { authState } = useOktaAuth();
  const { state, dispatch } = useSearch();
  const { filters, attributes, selectedIndexes, selectedSalesOrgs, enabledProductTypes } = state;

  // Allow removal when there are more filters than available attributes, or when there's more than 1 filter
  const disableButton = filters.length <= Math.min(attributes.length, 1);

  // Create a stable reference for other completed filters
  // Used in the facet loading function to get current context
  const otherCompletedFilters = useMemo(() => {
    return filters.filter(
      (filter) =>
        filter.id !== props.filter.id && // exclude self
        filter.attribute &&
        (filter.selectedValue || filter.lowerBound || filter.upperBound)
    );
  }, [filters, props.filter.id]);

  const [debouncedOtherCompletedFilters, setDebouncedOtherCompletedFilters] = useState(otherCompletedFilters);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedOtherCompletedFilters(otherCompletedFilters);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [otherCompletedFilters]);

  const facetParams: FacetParams = useMemo(
    () => ({
      bearerToken: authState?.accessToken?.accessToken ?? '',
      currentFilters: debouncedOtherCompletedFilters,
      attributeName: props.filter.attribute ? props.filter.attribute.facetDisplayValues : '',
      indexNames: selectedIndexes,
      salesOrgDivs: selectedSalesOrgs.map((org: ISalesOrg) => org.salesOrgId),
      enabledProductTypes: enabledProductTypes,
    }),
    [
      authState?.accessToken?.accessToken,
      debouncedOtherCompletedFilters,
      props.filter.attribute,
      selectedIndexes,
      selectedSalesOrgs,
      enabledProductTypes,
    ]
  );

  // Determine if the query should be enabled
  const isQueryEnabled =
    !props.disabled &&
    !!props.filter.attribute?.isFacetable &&
    !!authState?.accessToken?.accessToken &&
    selectedIndexes.length > 0;

  const { data: facets, isLoading, error } = useFacetValues(facetParams, isQueryEnabled);

  useEffect(() => {
    if (error) {
      toast.error('Error fetching facet values. Please try again later.');
    }
  }, [error]);

  const facetOptions = useMemo(() => {
    if (!facets) return [];
    const transformedData = facets.map((d: IFacetValue) => d.value).sort((a: string, b: string) => a.localeCompare(b));
    return [...new Set(transformedData)];
  }, [facets]);
  const handleAttributeChange = (_event: SyntheticEvent, value: IAttributeDto | null) => {
    dispatch({
      type: 'UPDATE_FILTER',
      payload: {
        id: props.filter.id,
        attribute: value ?? undefined,
        // Reset other values when attribute changes
        selectedValue: undefined,
        lowerBound: undefined,
        upperBound: undefined,
      },
    });
  };

  const handleValueChange = (_event: SyntheticEvent, newInputValue: string) => {
    dispatch({
      type: 'UPDATE_FILTER',
      payload: { ...props.filter, selectedValue: newInputValue || undefined },
    });
  };

  return (
    <Stack
      boxSizing="border-box"
      direction="row"
      position="relative"
      sx={{ width: '100%', height: '100%', padding: '0px 8px 16px' }}
      spacing={2}
    >
      <Stack direction={'row'} sx={{ minWidth: '40%' }}>
        <Autocomplete
          fullWidth
          className="w-100"
          disabled={props.disabled}
          value={props.filter.attribute ?? null}
          options={attributes.filter(
            (a) => !filters.some((f) => f.attribute?.attributeName === a.attributeName && f.id !== props.filter.id)
          )}
          getOptionLabel={(option) => option.displayName}
          isOptionEqualToValue={(option, value) => option.attributeName === value.attributeName}
          renderInput={(params) => (
            <TextField {...params} label="Attribute" inputRef={props.attributeRef} InputLabelProps={{ shrink: true }} />
          )}
          onChange={handleAttributeChange}
        />
      </Stack>
      <Box sx={{ minWidth: '40%' }}>
        {props.filter.attribute?.isFacetable && (
          <Autocomplete
            fullWidth
            className="w-100"
            disabled={props.disabled}
            freeSolo
            loading={isLoading}
            value={props.filter.selectedValue ?? ''}
            options={facetOptions}
            filterOptions={(options, params) => {
              if (options.length === 0) {
                return [];
              }

              const filterText = params.inputValue || props.filter.selectedValue || '';
              if (!filterText) {
                return options;
              }

              // Filter facet options based on input
              return options.filter((option) => (option as string).toLowerCase().includes(filterText.toLowerCase()));
            }}
            onInputChange={handleValueChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Value"
                placeholder={facetOptions.length === 0 ? 'Type to search...' : 'Select or type...'}
                onKeyDown={props.handleValueKeyDown}
                autoFocus={props.autoFocusValue}
              />
            )}
            onChange={(_e, value) => {
              if (typeof value === 'string') {
                dispatch({ type: 'UPDATE_FILTER', payload: { ...props.filter, selectedValue: value || undefined } });
              }
            }}
            // Prevent MUI warning when no options are available
            isOptionEqualToValue={(option, value) => option === value}
          />
        )}
        {!props.filter.attribute?.isFacetable &&
          (props.filter.attribute?.dataType === 'Int' || props.filter.attribute?.dataType === 'Double') && (
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Lower Bound"
                type="number"
                disabled={props.disabled}
                value={props.filter.lowerBound ?? null}
                onChange={(e) =>
                  dispatch({ type: 'UPDATE_FILTER', payload: { ...props.filter, lowerBound: Number(e.target.value) } })
                }
              />
              <TextField
                fullWidth
                label="Upper Bound"
                type="number"
                disabled={props.disabled}
                value={props.filter.upperBound ?? null}
                onKeyDown={props.handleValueKeyDown}
                onChange={(e) =>
                  dispatch({ type: 'UPDATE_FILTER', payload: { ...props.filter, upperBound: Number(e.target.value) } })
                }
              />
            </Stack>
          )}
        {!props.filter.attribute?.isFacetable && props.filter.attribute?.dataType === 'String' && (
          <TextField
            fullWidth
            label="Value"
            placeholder={props.filter.attribute?.placeholder}
            disabled={props.disabled}
            autoFocus={props.autoFocusValue}
            value={props.filter.selectedValue ?? ''}
            onKeyDown={props.handleValueKeyDown}
            onChange={(e) =>
              dispatch({ type: 'UPDATE_FILTER', payload: { ...props.filter, selectedValue: e.target.value } })
            }
          />
        )}
      </Box>
      {props.filter.attribute && (
        <IconButton onClick={props.handleRemove} disabled={disableButton} sx={{ alignSelf: 'flex-start' }}>
          <DeleteForeverIcon />
        </IconButton>
      )}
    </Stack>
  );
};

export default AttributeValueSelector;
